<script setup>
import {ref, onMounted} from 'vue';
import {definePage} from "unplugin-vue-router/runtime";
import createApiService from "@/services/api.js";
import {useRoute, useRouter} from "vue-router";
import {useAuthenticationStore} from "@/stores/authentication.js";
import {useAdminStore} from "@/stores/admin.js";

definePage({
  meta: {
    name: 'nieuwe-taak',
    title: 'Nieuwe taak - Selectie',
    requiresAuth: true,
    admin: true,
    layout: 'admin-task',
  },
})

const route = useRoute();
const router = useRouter();
const api = createApiService(router);
const authStore = useAuthenticationStore();
const adminStore = useAdminStore();
const loading = ref(true);
const errors = ref([]);

const taskId = ref(null);
const createTask = async (templateId = false) => {
  loading.value = true;
  errors.value = [];

  try {
    const response = await api.post('createadmintaskdraft', {
      id: templateId,
      adminId: authStore.getCurrentUserId,
      clientId: adminStore.getClient,
    })
    if (response.status === 200) {
      setTask(response.data.data);
    }
    else {
      errors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    errors.value = e.response?.status === 400 ? [e.response.data.message] : ['Er is iets misgegaan. Probeer het later nog eens.'];
  } finally {
    loading.value = false;
  }
}
const setTask = (id) => {
  taskId.value = id;
  setCurrentStep(1);
}

const draftTasks = ref([]);
const getDrafts = async () => {
  loading.value = true;

  try {
    const response = await api.post('getadmindrafttasks', {userId: authStore.getCurrentUserId})
    if (response.status === 200) {
      draftTasks.value = response.data.data;
    }
    else {
      errors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    errors.value = e.response?.status === 400 ? [e.response.data.message] : ['Er is iets misgegaan. Probeer het later nog eens.'];
  } finally {
    loading.value = false;
  }
};
const removeDraft = async (draftId) => {
  loading.value = true;

  try {
    const response = await api.post('deleteadmintask', {id: draftId})
    if (response.status === 200) {
      await getDrafts();
    }
    else {
      errors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    errors.value = e.response?.status === 400 ? [e.response.data.message] : ['Er is iets misgegaan. Probeer het later nog eens.'];
  } finally {
    loading.value = false;
  }
}

const templateTasks = ref([]);
const getTemplates = async () => {
  loading.value = true;
  try {
    const response = await api.get('getadmintemplatetasks')
    if (response.status === 200) {
      templateTasks.value = response.data.data;
    }
    else {
      errors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    errors.value = e.response?.status === 400 ? [e.response.data.message] : ['Er is iets misgegaan. Probeer het later nog eens.'];
  } finally {
    loading.value = false;
  }
}

const stepperRef = ref(null);

const currentStep = ref(null);
const setCurrentStep = (step) => {
  currentStep.value = step;
}

const prev = async () => {
  if (currentStep.value === null) {
    await router.push('/admin')
  }
  else if (currentStep.value === 1) {
    currentStep.value = null;
    await getDrafts();
    await getTemplates();
  }
  else {
    stepperRef.value?.setStep(currentStep.value - 1);
  }
}

onMounted(async () => {
  await getDrafts();
  await getTemplates();
})
</script>

<template>
  <task-header title="Nieuwe taak" @click="prev()"/>

  <v-alert
    v-if="errors.length > 0"
    color="error"
    class="mx-auto"
    max-width="500"
    variant="elevated"
  >
    <template #text>
            <span
              class="d-block"
              v-for="(error, index) in errors"
              :key="index"
            >
              {{ error }}
            </span>
    </template>
  </v-alert>

  <v-sheet
    v-if="currentStep === null"
    class="bg-primary text-center px-6 mx-auto"
    max-width="400"
  >
    <v-sheet
      v-if="draftTasks.length > 0"
      class="bg-primary text-center mb-4 pt-8"
    >
      <p class="mb-4">Nog niet afgerond:</p>
      <edit-task
        v-for="(draftTask, index) in draftTasks"
        :key="draftTask" :task="draftTask"
        :is-last="index === draftTask.length - 1"
        is-draft
        @click="router.push(`/admin/taken/${draftTask.id}`)"
        @delete="removeDraft(draftTask.id)"
      />
    </v-sheet>

    <v-sheet class="bg-primary text-center mb-4 pt-8">
      <p class="mb-4">Begin met een lege taak:</p>
      <new-task @click="createTask()"/>
    </v-sheet>

    <v-sheet
      v-if="templateTasks.length > 0"
      class="bg-primary text-center mb-4 pt-8"
    >
      <p class="mb-4">Of kies een van de templates:</p>
      <edit-task
        v-for="(templateTask, index) in templateTasks"
        :key="templateTask" :task="templateTask"
        :is-last="index === templateTask.length - 1"
        is-template
        @click="createTask(templateTask.id)"
      />
    </v-sheet>
  </v-sheet>

  <task-stepper
    v-else
    ref="stepperRef"
    :task-id="taskId"
    @update="setCurrentStep"
  />

</template>

<style scoped>

</style>
